import { Grid } from '@mui/material';
import { Location } from '@nx-smartmonkey/shared/domain';
import { validateCoordinates } from '@nx-smartmonkey/shared/helpers';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import lodash from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import LocationOnIcon from '../assets/svg/LocationOnIcon';
import { useRetrieveActiveLanguage } from '../hooks/translations/useRetrieveActiveLanguage';
import { useErrorMiddleware } from '../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../hooks/useShowSnackbarMessage';
import { getAutocompleteGeocode, getLocationFromCoordinates, getLocationFromId } from '../services/requests';
import InputText from './InputText';
import { Text } from './Text';

let sessionToken: string | undefined;

type InputLocationProps = {
  projectId?: string;
  placeholderIntlId?: string;
  placeholderIntlValues?: Record<string, string>;
  fieldValue?: Location;
  disabled?: boolean;
  onUpdate: (location: Location | null) => void;
};

export const InputLocation = ({
  projectId,
  placeholderIntlId,
  placeholderIntlValues,
  fieldValue,
  disabled = false,
  onUpdate,
}: InputLocationProps) => {
  const [value, setValue] = useState<Location | null>(fieldValue ?? null);
  const [inputValue, setInputValue] = useState(fieldValue?.label ?? ``);
  const [options, setOptions] = useState<Location[]>(fieldValue ? [fieldValue] : []);
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { activeLanguage } = useRetrieveActiveLanguage();

  useEffect(() => {
    if (fieldValue && !lodash.isEqual(fieldValue, value)) {
      setValue(fieldValue);
      setInputValue(fieldValue.label ?? ``);
      setOptions([]);
    }
    if (!fieldValue) {
      setValue(null);
      setOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  const manageAxiosError = (error: any) => {
    if (error.response?.status === 401) {
      sessionExpiredLogout();
    } else if (error.response?.status === 429) {
      showSnackbarMessage({
        messageIntlId: `infoerror.highway.user.error.too_many_requests`,
      });
    } else {
      showSnackbarMessage({
        messageIntlId: `infoerror.${error?.response?.data?.messageId ?? `network_error`}`,
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOptionsDelayed = useCallback(
    lodash.debounce(async (text, callback) => {
      try {
        const inputValueSplited = text.split(`,`);
        let newOptions;
        if (
          inputValueSplited.length === 2 &&
          validateCoordinates({ lat: Number(inputValueSplited[0]), lng: Number(inputValueSplited[1]) })
        ) {
          const [lat, lng] = inputValueSplited;
          newOptions = await getLocationFromCoordinates({
            coordinates: [{ lat, lng }],
            lang: activeLanguage,
          });
        } else {
          if (!sessionToken) sessionToken = uuidv4();
          newOptions = await getAutocompleteGeocode({
            searchValue: text,
            projectId: projectId ? projectId : undefined,
            sessionToken,
          });
        }
        callback(newOptions);
      } catch (error: any) {
        manageAxiosError(error);
      }
    }, 500),
    []
  );

  useEffect(() => {
    let active = true;

    if (inputValue === `` || inputValue === fieldValue?.label) {
      setOptions([]);
      return undefined;
    }

    if (active) {
      getOptionsDelayed(inputValue, (newOptions: any[]) => {
        setOptions(newOptions.slice(0, 3));
      });
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputValue, getOptionsDelayed]);

  // // Select the content of the empty text
  // const processedEmptyText: string | undefined = emptyIntlId
  //   ? intl.formatMessage({ id: emptyIntlId }, emptyIntlValues)
  //   : emptyText;

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" flexGrow="1" width="100%">
      <InputText
        disabled={disabled}
        hoverBGColor="transparent"
        bGColor="transparent"
        startAdornment={
          inputValue ? (
            <div
              style={{
                marginLeft: `6px`,
                marginTop: `5px`,
              }}
            >
              <LocationOnIcon height="20px" width="20px" fill={RoutalPalette.secondary60} />
            </div>
          ) : (
            <div
              style={{
                marginLeft: `6px`,
                marginTop: `5px`,
              }}
            >
              <LocationOnIcon height="20px" width="20px" fill={RoutalPalette.secondary00} />
            </div>
          )
        }
        fieldValue={value?.label ?? ``}
        placeholderIntlId={placeholderIntlId}
        placeholderIntlValues={placeholderIntlValues}
        onChange={(value) => {
          setInputValue(value);
          if (value === ``) {
            onUpdate(null);
          }
        }}
        inputContainerStyles={{ height: `49px` }}
        inputStyles={{ paddingLeft: `40px` }}
      />
      <Grid>
        {options.length > 0 &&
          options.map((option, index) => (
            <Grid
              key={option.label}
              height="50px"
              borderBottom={index < options.length - 1 ? `1px solid ${RoutalPalette.neutral10}` : ``}
              container
              alignItems="center"
              paddingLeft="30px"
              onClick={async () => {
                setValue(option);
                setInputValue(option.label ?? ``);

                if (option?.lat && option?.lng) {
                  await onUpdate(option);
                } else if (option === null) {
                  await onUpdate(null);
                } else if (option.location_id) {
                  try {
                    const resp = await getLocationFromId(option.location_id, sessionToken);
                    sessionToken = undefined;
                    await onUpdate(Location.create(resp));
                  } catch (err) {
                    manageAxiosError(err);
                  }
                }
              }}
            >
              <Text style={{ color: RoutalPalette.secondary00 }}>{option.label}</Text>
            </Grid>
          ))}
      </Grid>
    </Grid>
  );
};
